import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle, Factory, Plane, Bot, Award, Trophy, Glasses, Box, Building2, GraduationCap, Sparkles } from 'lucide-react';
import FullScreenHeroSlider from '../components/FullScreenHeroSlider';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Bot,
  Plane,
  Factory,
  Award,
  Trophy,
  Glasses,
  Box,
  Building2,
  GraduationCap,
};

const Home = () => {
  const [hero, setHero] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);
  const [about, setAbout] = useState(null);
  const [services, setServices] = useState([]);
  const [valueProposition, setValueProposition] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [heroRes, heroSlidesRes, aboutRes, servicesRes, vpRes] = await Promise.all([
        axios.get(`${API_URL}/hero`),
        axios.get(`${API_URL}/hero-slides?is_active=true`),
        axios.get(`${API_URL}/about`),
        axios.get(`${API_URL}/services?is_active=true`),
        axios.get(`${API_URL}/value-proposition`),
      ]);
      setHero(heroRes.data);
      setHeroSlides(heroSlidesRes.data);
      setAbout(aboutRes.data);
      setServices(servicesRes.data);
      setValueProposition(vpRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Default fallback slides if no slides are in DB
  const defaultSlides = [
    {
      image: '/gallery/lab_1.jpg',
      category: 'Advanced Engineering Labs',
      title: hero?.heading || 'UAV Geo Solutions',
      description: hero?.subheading || 'Transforming Institutions into Future-Ready Technology Centers',
      buttons: [
        { text: hero?.cta_primary_text || 'Explore Services', link: '/services', primary: true },
        { text: hero?.cta_secondary_text || 'Get Proposal', link: '/contact', primary: false }
      ]
    },
    {
      image: '/gallery/team_drone.jpg',
      category: 'Drone Technology',
      title: 'Aerial Innovation',
      description: 'Cutting-edge drone solutions for surveying, mapping, and autonomous operations',
      buttons: [
        { text: 'View Capabilities', link: '/services', primary: true },
        { text: 'Contact Us', link: '/contact', primary: false }
      ]
    },
    {
      image: '/gallery/robotics/robot-stands-rocky-surface-holding-drone-as-sun-sets-casting-warm-light-across-sky-robot-figure-standing-412566932.webp',
      category: 'Robotics & AI',
      title: 'Intelligent Automation',
      description: 'Advanced robotics solutions powered by artificial intelligence for next-gen industries',
      buttons: [
        { text: 'Discover More', link: '/services', primary: true },
        { text: 'Request Demo', link: '/contact', primary: false }
      ]
    },
    {
      image: '/gallery/lab_11.jpg',
      category: 'IoT Solutions',
      title: 'Connected Future',
      description: 'Smart IoT ecosystems transforming institutions into intelligent technology hubs',
      buttons: [
        { text: 'Learn More', link: '/services', primary: true },
        { text: 'Get Started', link: '/contact', primary: false }
      ]
    },
    {
      image: '/gallery/lab_16.jpg',
      category: 'Training & Education',
      title: 'Empower Tomorrow',
      description: 'Comprehensive training programs building the next generation of tech leaders',
      buttons: [
        { text: 'View Programs', link: '/services', primary: true },
        { text: 'Enroll Now', link: '/contact', primary: false }
      ]
    }
  ];

  // Transform API slides to match slider component format
  const formattedSlides = heroSlides.length > 0 
    ? heroSlides.map(slide => ({
        image: slide.image_url,
        category: slide.category,
        title: slide.heading,
        description: slide.subheading,
        buttons: [
          { text: slide.cta_primary_text, link: slide.cta_primary_link, primary: true },
          { text: slide.cta_secondary_text, link: slide.cta_secondary_link, primary: false }
        ]
      }))
    : defaultSlides;

  if (!hero || !about) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0e27]">
      <div className="text-center">
        <Sparkles size={48} className="text-primary animate-pulse mx-auto mb-4" />
        <div className="text-xl font-heading text-primary">Loading...</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <SEO 
        title="UAV Geo Solutions - Transforming Institutions into Future-Ready Technology Centers"
        description="India's leading provider of Drone Labs, Robotics & IoT Labs, DGCA RPTO Training, AR/VR Labs, 3D Printing Labs, and more. Transform your institution with cutting-edge technology."
        keywords="drone lab setup, robotics lab, IoT lab, DGCA RPTO training, drone pilot training, technology labs India, UAV solutions, Greater Noida"
        url="/"
      />
      {/* Full Screen Hero Slider */}
      <div className="pt-[72px]" data-testid="hero-section">
        <FullScreenHeroSlider 
          slides={formattedSlides} 
          interval={7000}
          showScrollIndicator={true}
        />
      </div>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#0a0e27] via-[#0f1535] to-[#0a0e27] relative overflow-hidden" data-testid="about-section">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-block mb-4"
            >
              <span className="text-xs uppercase tracking-[0.3em] text-primary font-semibold">About Us</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold uppercase tracking-tight mb-6 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              {about.title}
            </h2>
            <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-4xl mx-auto mb-8">
              {about.overview}
            </p>
            <div className="glass-heavy rounded-2xl p-8 max-w-3xl mx-auto border border-primary/20">
              <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Our Mission</h3>
              <p className="text-base md:text-lg text-gray-300 font-light leading-relaxed">{about.mission}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#0f1629] via-[#1a1f3a] to-[#0f1629] relative overflow-hidden" data-testid="services-section">
        {/* Grid Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-block mb-4"
            >
              <span className="text-xs uppercase tracking-[0.3em] text-primary font-semibold">Our Services</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold uppercase tracking-tight mb-6 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Core Service Verticals
            </h2>
            <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-3xl mx-auto">
              Comprehensive technology solutions for modern institutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Box;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`service-card-${index}`}
                  className="group glass hover-lift rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 border border-white/5 hover:border-primary/30 relative overflow-hidden"
                >
                  {/* Hover Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  <div className="relative z-10">
                    <div className="mb-4 relative">
                      <div className="absolute inset-0 blur-xl bg-primary/20 group-hover:bg-primary/40 transition-all"></div>
                      <Icon size={40} className="text-primary relative z-10" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-2xl md:text-2xl font-heading font-medium mb-4 group-hover:text-primary transition-colors">{service.title}</h3>
                    <p className="text-sm text-gray-400 mb-6 leading-relaxed">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-xs text-gray-400">
                          <CheckCircle size={16} className="text-primary flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                size="lg"
                data-testid="view-all-services-button"
                className="bg-gradient-to-r from-primary to-indigo-500 text-black hover:from-primary-hover hover:to-indigo-600 uppercase tracking-widest font-bold shadow-xl shadow-primary/30 group"
              >
                View All Services
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Proposition & Stats */}
      {valueProposition && (
        <section className="py-24 md:py-32 bg-[#0B1121]" data-testid="value-proposition-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-heading font-semibold uppercase tracking-tight mb-12">
                {valueProposition.title}
              </h2>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                {Object.values(valueProposition.stats).map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    data-testid={`stat-${index}`}
                    className="glass-heavy rounded-2xl p-8"
                  >
                    <p className="text-4xl md:text-5xl font-code font-bold text-primary mb-2">{stat.split(' ')[0]}</p>
                    <p className="text-sm uppercase tracking-wider text-gray-400">{stat.split(' ').slice(1).join(' ')}</p>
                  </motion.div>
                ))}
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {valueProposition.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    data-testid={`benefit-${index}`}
                    className="glass rounded-xl p-6 text-left"
                  >
                    <CheckCircle size={24} className="text-primary mb-3" />
                    <p className="text-base text-gray-300">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#020408] to-[#0B1121]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-6">
              Ready to Transform Your Institution?
            </h2>
            <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed mb-12">
              Let&apos;s discuss how we can help you build a future-ready technology center.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                data-testid="cta-contact-button"
                className="bg-primary text-black hover:bg-primary-hover uppercase tracking-widest font-bold px-12 py-6 text-base"
              >
                Contact Us Today
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
